import React, { useState, useEffect } from 'react';
import api from '../api/axiosInstance';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { ArrowsClockwise, CheckCircle } from '@phosphor-icons/react';
import { Card } from '../components/ui/card';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0
}).format(v || 0);

export const ChannelsPage = () => {
  const [channels, setChannels] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [deficitStatus, setDeficitStatus] = useState(null);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 3000);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    try {
      const [cR, aR, pR, dR] = await Promise.allSettled([
        api.get(`${API}/channels`),
        api.get(`${API}/accounts`),
        api.get(`${API}/payments?status=PENDING`),
        api.get(`${API}/liquidity/deficit-status`),
      ]);
      if (cR.status === 'fulfilled') setChannels(cR.value.data);
      if (aR.status === 'fulfilled') setAccounts(aR.value.data);
      if (pR.status === 'fulfilled') setPayments(pR.value.data);
      if (dR.status === 'fulfilled') setDeficitStatus(dR.value.data);
    } catch (e) { console.error('Fetch data error:', e); }
  };

  const getSlave = (channelId) =>
    accounts.find(a => a.channel_id === channelId && (a.account_type === 'CHANNEL' || a.account_type === 'SLAVE'));

  const getNetRequired = (channelId) => {
    const debits = payments
      .filter(p => p.channel_id === channelId && p.payment_type === 'DEBIT' && p.queue_type === 'CURRENT')
      .reduce((s, p) => s + p.amount, 0);
    const credits = payments
      .filter(p => p.channel_id === channelId && p.payment_type === 'CREDIT' && p.queue_type === 'CURRENT')
      .reduce((s, p) => s + p.amount, 0);
    return Math.max(0, debits - credits);
  };

  const getQueueCounts = (channelId) => ({
    currIn:  payments.filter(p => p.channel_id === channelId && p.queue_type === 'CURRENT' && p.payment_type === 'CREDIT').length,
    currOut: payments.filter(p => p.channel_id === channelId && p.queue_type === 'CURRENT' && p.payment_type === 'DEBIT').length,
    futIn:   payments.filter(p => p.channel_id === channelId && p.queue_type === 'FUTURE'  && p.payment_type === 'CREDIT').length,
    futOut:  payments.filter(p => p.channel_id === channelId && p.queue_type === 'FUTURE'  && p.payment_type === 'DEBIT').length,
  });

  const bankNames = [...new Set(payments.map(p => p.sender).filter(Boolean))].sort();
  const bankQueueData = bankNames.map(bank => ({
    bank,
    current: payments.filter(p => p.sender === bank && p.queue_type === 'CURRENT').length,
    future:  payments.filter(p => p.sender === bank && p.queue_type === 'FUTURE').length,
    total:   payments.filter(p => p.sender === bank).length,
  })).filter(b => b.total > 0);

  const chartData = channels.map(ch => ({
    name: ch.code,
    actual:   Math.round(getSlave(ch.id)?.balance || 0),
    required: Math.round(getNetRequired(ch.id)),
  }));

  const totalPending = payments.length;

  // Flash notification helper — same logic as Dashboard (show for 12s)
  const getFlashNotification = () => {
    if (!deficitStatus?.notifications?.length) return null;
    const latest = deficitStatus.notifications[0];
    const age = (Date.now() - new Date(latest.timestamp).getTime()) / 1000;
    return age <= 12 ? latest : null;
  };
  const flashNotification = getFlashNotification();
  const hasAlerts = deficitStatus?.has_active_deficits;

  return (
    <div className="space-y-4 fade-in">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-black tracking-tight text-gray-900">Channels</h1>
          <p className="text-xs text-gray-500 mt-0.5 uppercase tracking-wide font-medium">
            Liquidity &amp; pending queues per channel
          </p>
        </div>
        <span className="text-xs font-mono text-gray-400 bg-gray-100 px-3 py-1.5 rounded-full">
          {channels.length} channels &middot; {totalPending} pending
        </span>
      </div>

      {/* Step-by-step borrowing notifications */}
      {!hasAlerts && flashNotification && (
        <div
          className={`rounded-xl border-2 p-3 ${
            flashNotification.type === 'AUTO_RESOLVED'     ? 'border-green-300 bg-green-50'   :
            flashNotification.type === 'BORROW_CREDITED'   ? 'border-blue-200 bg-blue-50'     :
            flashNotification.type === 'INTEREST_DEDUCTED' ? 'border-amber-200 bg-amber-50'   :
            flashNotification.type === 'CHANNEL_FUNDED'    ? 'border-emerald-200 bg-emerald-50':
            'border-blue-300 bg-blue-50'
          }`}
          data-testid="channel-deficit-notification"
        >
          {flashNotification.type === 'AUTO_RESOLVED' ? (
            <div className="flex items-start gap-2">
              <ArrowsClockwise size={16} weight="bold" className="text-green-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-green-700 font-black text-sm">Deficit auto-resolved — {flashNotification.channel_name}</p>
                <p className="text-green-600 text-xs font-mono mt-0.5">{fmt(flashNotification.deficit_amount)} from {flashNotification.transferred_from?.map(t => t.channel).join(', ') || 'channel surplus'}</p>
              </div>
            </div>
          ) : flashNotification.type === 'BORROW_CREDITED' ? (
            <div className="flex items-start gap-2">
              <CheckCircle size={16} weight="bold" className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-blue-700 font-bold text-sm">Loan credited to pool</p>
                <p className="text-blue-600 text-xs font-mono mt-0.5">{flashNotification.message}</p>
              </div>
            </div>
          ) : flashNotification.type === 'INTEREST_DEDUCTED' ? (
            <div className="flex items-start gap-2">
              <CheckCircle size={16} weight="bold" className="text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-amber-700 font-bold text-sm">Interest deducted from pool</p>
                <p className="text-amber-600 text-xs font-mono mt-0.5">{flashNotification.message}</p>
              </div>
            </div>
          ) : flashNotification.type === 'CHANNEL_FUNDED' ? (
            <div className="flex items-start gap-2">
              <CheckCircle size={16} weight="bold" className="text-emerald-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-emerald-700 font-bold text-sm">Channel funded</p>
                <p className="text-emerald-600 text-xs font-mono mt-0.5">{flashNotification.message}</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <CheckCircle size={16} weight="bold" className="text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-blue-700 font-bold text-sm">Borrowing approved — simulation resuming</p>
                <p className="text-blue-600 text-xs font-mono mt-0.5">{fmt(flashNotification.deficit_amount)} in {flashNotification.channel_name} via {flashNotification.source === 'CENTRAL_BANK' ? 'Central Bank' : 'Money Market'}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actual vs Required Chart */}
      <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="channel-liquidity-chart">
        <div className="px-4 py-3 border-b border-gray-200">
          <h2 className="text-sm font-semibold text-gray-900">Balance vs Net Required</h2>
          <p className="text-xs text-gray-500 mt-0.5">Channel account balance vs net pending outbound (Current queue)</p>
        </div>
        <div className="p-4">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} barCategoryGap="30%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="name" stroke="#9CA3AF" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }} />
              <YAxis stroke="#9CA3AF" tick={{ fontSize: 11, fontFamily: 'JetBrains Mono' }}
                tickFormatter={v => `$${(v / 1000000).toFixed(1)}M`} width={48} />
              <Tooltip contentStyle={{ backgroundColor: '#fff', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '11px', fontFamily: 'JetBrains Mono' }}
                formatter={v => [fmt(v), '']} />
              <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '11px' }} />
              <Bar dataKey="actual"   name="Actual Balance"      fill="#6366F1" radius={[4, 4, 0, 0]} />
              <Bar dataKey="required" name="Net Required (Current)" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Bank Queue Overview */}
      {bankQueueData.length > 0 && (
        <Card className="p-0 overflow-hidden bg-white border border-gray-200 rounded-lg" data-testid="bank-queue-overview">
          <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
            <div>
              <h2 className="text-xs font-semibold text-gray-900 uppercase tracking-wide">Bank Queue Status</h2>
              <p className="text-xs text-gray-500 mt-0.5">Pending payments per bank — Current &amp; Future queues</p>
            </div>
            <span className="text-xs font-mono text-gray-400 bg-white border border-gray-200 px-2.5 py-1 rounded-full">{bankQueueData.length} banks</span>
          </div>
          <div className="grid grid-cols-[1fr_3.5rem_3.5rem_3.5rem] px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs text-gray-500 uppercase tracking-wide font-semibold">
            <span>Bank</span>
            <span className="text-center text-emerald-600">Curr</span>
            <span className="text-center text-indigo-600">Fut</span>
            <span className="text-right">Total</span>
          </div>
          <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
            {bankQueueData.map(b => (
              <div key={b.bank} className="grid grid-cols-[1fr_3.5rem_3.5rem_3.5rem] px-4 py-2 hover:bg-gray-50 items-center" data-testid={`bank-queue-row-${b.bank}`}>
                <span className="text-xs font-black font-mono text-gray-800">{b.bank}</span>
                <span className={`text-center text-xs font-bold font-mono ${b.current > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>{b.current}</span>
                <span className={`text-center text-xs font-bold font-mono ${b.future > 0 ? 'text-indigo-600' : 'text-gray-300'}`}>{b.future}</span>
                <span className="text-right text-xs font-bold font-mono text-gray-700">{b.total}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Channel Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {channels.map(ch => {
          const slave    = getSlave(ch.id);
          const balance  = slave?.balance || 0;
          const required = getNetRequired(ch.id);
          const hasGap   = required > balance;

          return (
            <Card
              key={ch.id}
              className={`p-4 bg-white border rounded-lg ${hasGap ? 'border-amber-300' : 'border-gray-200'}`}
              data-testid={`channel-card-${ch.code}`}
            >
              {/* Name + status */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-bold text-gray-500 bg-gray-100 border border-gray-200 rounded px-2 py-0.5">
                    {ch.code}
                  </span>
                  <span className="text-sm font-bold text-gray-900">{ch.name}</span>
                </div>
                <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full border ${
                  hasGap
                    ? 'bg-amber-50 border-amber-200 text-amber-700'
                    : 'bg-emerald-50 border-emerald-200 text-emerald-700'
                }`}>
                  {hasGap ? 'Deficit' : 'Funded'}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="bg-gray-50 rounded-md p-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Balance</p>
                  <p className="text-xs font-black font-mono text-gray-900 truncate">{fmt(balance)}</p>
                </div>
                <div className="bg-gray-50 rounded-md p-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-0.5">Net Required</p>
                  <p className={`text-xs font-black font-mono truncate ${hasGap ? 'text-amber-600' : 'text-gray-900'}`}>
                    {fmt(required)}
                  </p>
                </div>
              </div>

              {/* Bank queue breakdown per channel — bank-wise */}
              {(() => {
                const chPmts = payments.filter(p => p.channel_id === ch.id);
                const banks = [...new Set(chPmts.map(p => p.sender).filter(Boolean))].sort();
                const bankRows = banks.map(b => ({
                  bank: b.replace('BANK-', ''),
                  current: chPmts.filter(p => p.sender === b && p.queue_type === 'CURRENT').length,
                  future:  chPmts.filter(p => p.sender === b && p.queue_type === 'FUTURE').length,
                })).filter(r => r.current + r.future > 0);

                if (!bankRows.length) return <p className="text-xs text-gray-400 text-center pt-1 font-mono">No pending payments</p>;
                return (
                  <div className="mt-1">
                    <div className="grid grid-cols-[1fr_2.5rem_2.5rem] text-xs text-gray-400 uppercase tracking-wide font-semibold pb-1 border-b border-gray-100 mb-0.5">
                      <span>Bank</span>
                      <span className="text-center text-emerald-500">Curr</span>
                      <span className="text-center text-indigo-500">Fut</span>
                    </div>
                    {bankRows.map(b => (
                      <div key={b.bank} className="grid grid-cols-[1fr_2.5rem_2.5rem] py-0.5" data-testid={`ch-bank-${ch.code}-${b.bank}`}>
                        <span className="text-xs font-mono font-black text-gray-700">{b.bank}</span>
                        <span className={`text-center text-xs font-mono font-bold ${b.current > 0 ? 'text-emerald-600' : 'text-gray-300'}`}>{b.current}</span>
                        <span className={`text-center text-xs font-mono font-bold ${b.future > 0 ? 'text-indigo-600' : 'text-gray-300'}`}>{b.future}</span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
